import { useEffect } from 'react';

import { AskChatSessionCategory } from 'store/api/ask-klue/chat-sessions';
import {
  setAskKluePendingQuestion,
  setAskKlueIsModernThread,
  setAskKlueResearchMode,
  setAskKlueThreadId,
  useAskKlueIsModernThread,
} from 'store/views/dashboard';

import useAskKlue from '../use-ask-klue/useAskKlue';

function useAskKlueRestoreSession() {
  const { askKlueQueryParams, setAskKlueQueryParams } = useAskKlue();
  const { askKlueSessionId, askKlueCategory, askKlueSourceApi } =
    askKlueQueryParams;
  const categoryEnum =
    AskChatSessionCategory[
      askKlueCategory?.toUpperCase() as keyof typeof AskChatSessionCategory
    ];

  // Redux is the single source of truth for isModernThread during the session
  const isModernThread = useAskKlueIsModernThread();

  // Initialize Redux state from URL query params on page load
  // This handles restoring modern research threads from URL
  useEffect(() => {
    if (
      askKlueSourceApi === 'research-modern' &&
      askKlueSessionId &&
      categoryEnum === AskChatSessionCategory.RESEARCH
    ) {
      setAskKlueIsModernThread(true);
      setAskKlueThreadId(askKlueSessionId);
      setAskKlueResearchMode(true);
    }
  }, [askKlueSourceApi, askKlueSessionId, categoryEnum]);

  // Initialize Redux state from URL query params on page load
  // This handles restoring modern answer threads from URL.
  useEffect(() => {
    if (
      askKlueSourceApi === 'answer-modern' &&
      askKlueSessionId &&
      categoryEnum === AskChatSessionCategory.ASKKLUE
    ) {
      setAskKlueIsModernThread(true);
      setAskKlueThreadId(askKlueSessionId);
      setAskKlueResearchMode(false);
    }
  }, [askKlueSourceApi, askKlueSessionId, categoryEnum]);

  const setRestoreSession = (
    sessionId: string,
    category: AskChatSessionCategory,
    sourceApi?: string,
  ) => {
    setAskKluePendingQuestion(undefined);
    const isModern =
      sourceApi === 'research-modern' || sourceApi === 'answer-modern';

    // Set Redux state immediately - this is the source of truth
    setAskKlueIsModernThread(isModern);
    setAskKlueThreadId(sessionId);
    setAskKlueResearchMode(category === AskChatSessionCategory.RESEARCH);

    // Update URL query params for persistence
    setAskKlueQueryParams(
      {
        askKlueSessionId: sessionId,
        askKlueCategory: category.toLowerCase(),
        askKlueSourceApi: sourceApi,
      },
      'replaceIn',
    );
  };

  // Distinguish modern research vs modern answer sessions so consumers don't
  // wrongly route answer threads through useResearchChat (which would 404 against
  // /research/threads/{id}). Redux's isModernThread is true for both kinds; the
  // sourceApi query param disambiguates.
  const isModernResearchSession =
    isModernThread && askKlueSourceApi === 'research-modern';
  const isModernAnswerSession =
    isModernThread && askKlueSourceApi === 'answer-modern';

  return {
    restoreSessionId: askKlueSessionId,
    setRestoreSession,
    isModernResearchSession,
    isModernAnswerSession,
  };
}

export default useAskKlueRestoreSession;
